<template>
  <Transition name="drawer-slide">
    <div v-if="isOpen" class="WalkReveal" @click.self="emit('close')">
      <div ref="drawerRef" class="reveal-drawer">
        <div class="drawer-top-bar">
          <span v-if="canViewOnMap" class="header-map-link" @click="handleViewOnMap">{{
            localize('walk.reveal.view_on_map')
          }}</span>
          <div class="drawer-close" @click="handleClose">✕</div>
        </div>
        <div class="drawer-scroll-content" :class="{ 'no-footer': !canWalkReds }">
          <WalkRevealContent
            :summary="summary"
            :walk-duration-ms="props.walkDurationMs"
            :show-third-spot="showThirdSpot"
            :break-bank-ms="store.settings.breakBankMs"
            :start-spot-label="startSpotLabel"
            :reveal-spot-label="revealSpotLabel"
          />
        </div>
        <div v-if="canWalkReds" class="drawer-footer">
          <span class="drawer-cta walk-reds-cta" @click="handleWalkReds">{{ localize('walk.reveal.walk_reds') }}</span>
          <span class="drawer-cta finish-cta" @click="handleFinish">{{ localize('walk.reveal.finish') }}</span>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts" setup>
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import { localize } from '@/core/gambits';
import { platform } from '@/core/platform';
import { useWalkStore } from '@/stores/walk.store';
import { useDrawerGesture } from '@/views/composables/hooks/useDrawerGesture';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import WalkRevealContent from './WalkRevealContent.vue';

const props = defineProps<{
  isOpen: boolean;
  walkDurationMs?: number | null;
}>();

const emit = defineEmits<{
  close: [];
  walkReds: [];
  finish: [];
}>();

const router = useRouter();
const store = useWalkStore();
const summary = computed(() => store.lastSummary);
const drawerRef = ref<HTMLElement | null>(null);

useDrawerGesture(drawerRef, () => emit('close'));

const canWalkReds = computed(() => store.isRevealed && store.hasWrongRounds && store.wrongCount > 0);

const canViewOnMap = computed(() => !(platform.isOfflineCapable && !navigator.onLine));

const showThirdSpot = computed(() => false);

const startSpotLabel = computed(() => store.lastReveal?.startSpotName ?? summary.value?.startSpotName ?? null);

const revealSpotLabel = computed(() => store.lastReveal?.revealSpotName ?? summary.value?.revealSpotName ?? null);

function handleWalkReds() {
  emit('walkReds');
}

function handleFinish() {
  emit('finish');
}

function handleClose() {
  emit('close');
}

function handleViewOnMap() {
  store.requestShowWalkMarkers();
  emit('close');
  router.replace({ name: RouterNameType.LOCATIONS, query: { fit_markers: 'true' } });
}
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.WalkReveal {
  position: fixed;
  inset: 0;
  // Above the session map button (z-index 1000), which otherwise sits on this drawer.
  z-index: 1100;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
}

.reveal-drawer {
  position: relative;
  width: 100%;
  max-height: calc(100vh - env(safe-area-inset-top) - 1rem);
  max-height: calc(100dvh - env(safe-area-inset-top) - 1rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: linear-gradient(20deg, oklch(0.18 0 0) 0%, oklch(0.22 0 0) 50%, oklch(0.18 0 0) 100%);
  border-radius: 1.5rem 1.5rem 0 0;
  border: 1px solid var(--color-white-10);
  border-bottom: none;
  will-change: transform;
}

.drawer-top-bar {
  position: relative;
  flex-shrink: 0;
  height: 3rem;
  border-bottom: 1px solid var(--color-white-10);
}

.drawer-scroll-content {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &.no-footer {
    padding-bottom: var(--safe-bottom-offset);
  }
}

.drawer-close {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 1.5rem;
  z-index: 1;
  width: 2rem;
  height: 2rem;
  border-radius: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-white-50);
  font-size: 16px;
  transition: color 150ms ease;

  @include hover {
    color: var(--color-white);
  }

  @include press($transform: translateY(-50%));
}

.header-map-link {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 1.5rem;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-white-40);
  cursor: pointer;
  transition: color 150ms ease;

  @include hover {
    color: var(--color-white-70);
  }
}

.drawer-footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1.25rem 1.5rem max(1rem, var(--safe-bottom-offset));
  border-top: 1px solid var(--color-white-10);
}

.drawer-cta {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  height: 3rem;
  padding: 0 20px;
  border-radius: 2rem;
  transition: all 200ms linear;

  @include press;
}

.finish-cta {
  color: var(--color-white-70);
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press;
}

.walk-reds-cta {
  color: var(--color-incorrect);
  border: 1px solid rgba(255, 255, 255, 0.27);
  background-color: rgba(255, 255, 255, 0.1);

  @include hover {
    background-color: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.53);
  }

  @include press;
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: opacity 200ms ease;

  .reveal-drawer {
    transition: transform 200ms ease;
  }
}

.drawer-slide-enter-from,
.drawer-slide-leave-to {
  opacity: 0;

  .reveal-drawer {
    transform: translateY(100%);
  }
}
</style>
